import { createFileRoute, Link } from "@tanstack/react-router";
import { SearchBar } from "@/components/site/SearchBar";
import { ListingCard } from "@/components/site/ListingCard";
import {
  categories,
  locations,
  featuredListings,
  recentListings,
  blogPosts,
  categoryHref,
  locationHref,
  blogHref,
} from "@/lib/data";
import { ArrowRight, Star, Users, MapPin, Sparkles } from "lucide-react";
import { generateBreadcrumbSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "LetsListBusiness — Discover the Best Local Businesses Near You" },
      { name: "description", content: "Search trusted restaurants, coffee shops, home services, gyms and more across Austin, Portland, Brooklyn, Denver, Nashville and Seattle." },
      { property: "og:title", content: "LetsListBusiness — Discover the Best Local Businesses Near You" },
      { property: "og:description", content: "Search trusted local businesses across major U.S. cities. Real reviews, real recommendations." },
      { property: "og:image", content: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1200&q=80&auto=format&fit=crop" },
      { property: "og:url", content: SITE_URL + "/" },
      { name: "twitter:image", content: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1200&q=80&auto=format&fit=crop" },
    ],
    links: [{ rel: "canonical", href: SITE_URL + "/" }],
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "LetsListBusiness",
        "url": SITE_URL,
        "logo": SITE_URL + "/logo.png",
        "sameAs": [
          "https://facebook.com/letslistbusiness",
          "https://twitter.com/letslistbusiness",
          "https://instagram.com/letslistbusiness"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+1-800-555-0199",
          "contactType": "customer service"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "LetsListBusiness",
        "url": SITE_URL,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": SITE_URL + "/search?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      },
      generateBreadcrumbSchema([{ label: "Home", href: "/" }], SITE_URL),
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <div
          className="absolute inset-0 opacity-25 mix-blend-overlay"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1600&q=80&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative container mx-auto px-4 pb-16 pt-20 sm:pt-28 sm:pb-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
              <Sparkles className="h-3 w-3" /> Trusted by local communities
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] text-white sm:text-6xl">
              Find the best of <em className="not-italic text-accent">your city</em>,
              one block at a time.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-white/85">
              Real reviews from neighbors. Independent businesses. Service
              providers you'd actually recommend to a friend.
            </p>
            <div className="mt-8 max-w-3xl">
              <SearchBar />
            </div>
            <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/85">
              <span className="opacity-70">Popular:</span>
              {["restaurants", "coffee-shops", "home-services", "fitness"].map(
                (s) => {
                  const c = categories.find((x) => x.slug === s)!;
                  return (
                    <Link
                      key={s}
                      to="/category/$slug"
                      params={{ slug: s }}
                      className="rounded-full border border-white/25 px-3 py-1 transition-colors hover:bg-white/15"
                    >
                      {c.name}
                    </Link>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-border bg-surface">
        <div className="container mx-auto grid grid-cols-2 gap-6 px-4 py-8 sm:grid-cols-4">
          {[
            { icon: Users, label: "Active reviewers", value: "120K+" },
            { icon: MapPin, label: "Cities covered", value: `${locations.length}` },
            { icon: Star, label: "Avg. rating", value: "4.7" },
            { icon: Sparkles, label: "Listings", value: "10K+" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
                <s.icon className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display text-xl font-bold">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="container mx-auto px-4 py-16">
        <SectionHeader
          eyebrow="Browse"
          title="Top categories"
          link={{ href: "/category/", label: "All categories" }}
        />
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to="/category/$slug"
              params={{ slug: c.slug }}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-lg"
            >
              <div className="aspect-5/4 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <div className="text-2xl">{c.icon}</div>
                <div className="mt-1 font-display text-lg font-semibold">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Locations */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <SectionHeader
            eyebrow="Explore"
            title="Top locations"
            link={{ href: "/location/", label: "All cities" }}
          />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {locations.map((l) => (
              <Link
                key={l.slug}
                to="/location/$slug"
                params={{ slug: l.slug }}
                className="group relative block overflow-hidden rounded-2xl border border-border shadow-card transition-all hover:shadow-card-lg"
              >
                <div className="aspect-16/10 overflow-hidden">
                  <img
                    src={l.image}
                    alt={`${l.name}, ${l.state}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-linear-to-t from-black/75 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 text-white">
                  <div>
                    <h3 className="font-display text-2xl font-bold">{l.name}</h3>
                    <p className="text-sm text-white/85">{l.state}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="container mx-auto px-4 py-16">
        <SectionHeader
          eyebrow="Hand-picked"
          title="Featured listings"
          link={{ href: "/category/", label: "Browse all" }}
        />
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featuredListings.map((l) => (
            <ListingCard key={l.id} listing={l} />
          ))}
        </div>
      </section>

      {/* Recent */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <SectionHeader eyebrow="Just added" title="Recent listings" />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recentListings.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="container mx-auto px-4 py-16">
        <SectionHeader
          eyebrow="Local guides"
          title="From the blog"
          link={{ href: "/blog/", label: "All articles" }}
        />
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {blogPosts.slice(0, 3).map((p) => (
            <Link
              key={p.slug}
              {...blogHref(p.slug)}
              className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-lg"
            >
              <div className="aspect-16/10 overflow-hidden">
                <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="text-xs uppercase tracking-wider text-primary">
                  {categories.find((c) => c.slug === p.category)?.name} ·{" "}
                  {locations.find((l) => l.slug === p.location)?.name}
                </div>
                <h3 className="mt-2 font-display text-xl font-semibold leading-tight">
                  {p.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                  {p.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 pb-20">
        <div className="overflow-hidden rounded-3xl bg-gradient-warm p-10 text-center shadow-glow sm:p-16">
          <h2 className="font-display text-3xl font-bold text-accent-foreground sm:text-4xl">
            Run a local business?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-accent-foreground/85">
            Join thousands of independent businesses reaching neighbors who
            actually live nearby.
          </p>
          <Link
            to="/add-business"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background"
          >
            Add your business <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function SectionHeader({
  eyebrow,
  title,
  link,
}: {
  eyebrow: string;
  title: string;
  link?: { href: string; label: string };
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-primary">
          {eyebrow}
        </div>
        <h2 className="mt-1 font-display text-3xl font-bold sm:text-4xl">
          {title}
        </h2>
      </div>
      {link && (
        <Link
          to={link.href}
          className="hidden items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline-flex"
        >
          {link.label} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}
